<script setup lang="ts" generic="Item extends VCheckboxListItem">
import type { VCheckboxListGroup, VCheckboxListItem } from "./VCheckboxList.types";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";
import VCheckbox from "../Checkbox/VCheckbox.vue";
import VListCard from "../ListCard/VListCard.vue";
import VListCardRow from "../ListCard/VListCardRow.vue";

defineOptions({
  name: "VCheckboxList",
});

/**
 * A list of checkboxes in a `VListCard`: a header with "select all" and the selected count, optional search,
 * optional groups with a sub-header each, and one row per item with its label and subtitle. The v-model holds the
 * checked values in the order of the list.
 */
const {
  items = undefined,
  groups = undefined,
  name,
  title = undefined,
  selectAll = true,
  selectedLabel = undefined,
  columns = 1,
  searchable = false,
  searchPlaceholder = undefined,
  filter = undefined,
  loading = false,
  emptyText = undefined,
  searchEmptyText = undefined,
  maxHeight = undefined,
  disabled = false,
  ariaLabel = undefined,
  ariaDescribedby = undefined,
  invalid = false,
  testId = undefined,
} = defineProps<{
  /** The rows of a list without groups. Ignored when `groups` is set. */
  items?: Item[]
  /** Rows in groups, each with an optional sub-header. */
  groups?: VCheckboxListGroup<Item>[]
  /** Prefix of the checkbox names, unique per form: `<name>-<value>` and `<name>-select-all`. */
  name: string
  /** Header label when there is no "select all"; otherwise the accessible name of the list. */
  title?: string
  /** Shows a "select all" checkbox in the header. It acts on the rows the search shows, except disabled ones. */
  selectAll?: boolean
  /**
   * Text on the right of the header, such as "3 met toegang". Always shown when set; without it the header shows
   * the generic "N selected" while something is selected.
   */
  selectedLabel?: string
  /** Rows per line from the `md` breakpoint; use 2 for short labels such as column names. */
  columns?: 1 | 2
  /** Shows a search field above the list that filters on label and subtitle. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Replaces the default search match on label and subtitle. `query` is trimmed and lower case. */
  filter?: (item: Item, query: string) => boolean
  loading?: boolean
  /** Shown when there are no rows at all. */
  emptyText?: string
  /** Shown when the search matches no rows. Defaults to the generic "no results" text. */
  searchEmptyText?: string
  /** Scrolls the rows inside this height (a number is px); the header stays visible. */
  maxHeight?: number | string
  /** Disables every checkbox. */
  disabled?: boolean
  ariaLabel?: string
  /** Id of an element that describes the list, such as an error about the selection. */
  ariaDescribedby?: string
  /**
   * The selection has an error: the frame turns red and the list and its checkboxes get `aria-invalid`. Point
   * `ariaDescribedby` at the error message.
   */
  invalid?: boolean
  testId?: string
}>();

defineSlots<{
  /** Content between the checkbox and the label, such as a status dot. */
  "item-prefix"?: (props: { item: Item, group: VCheckboxListGroup<Item> }) => unknown
  /** Content at the end of a row, such as a chip. */
  "item-trailing"?: (props: { item: Item, group: VCheckboxListGroup<Item> }) => unknown
  /** Replaces `emptyText`. */
  "empty"?: () => unknown
  /** Controls on the right of the header, after the count. */
  "header-actions"?: () => unknown
  /** A bar under the rows. */
  "footer"?: () => unknown
}>();

const selected = defineModel<Item["value"][]>({ required: true });
const search = defineModel<string>("search", { default: "" });

const { t } = useI18n();
const a11yId = useId();

const allGroups = computed<VCheckboxListGroup<Item>[]>(() => groups ?? [{ key: "items", items: items ?? [] }]);
const allValues = computed(() => allGroups.value.flatMap(group => group.items.map(item => item.value)));
const selectedSet = computed(() => new Set<Item["value"]>(selected.value));

const query = computed(() => search.value.trim().toLowerCase());

function matches(item: Item): boolean {
  if (!query.value) {
    return true;
  }
  if (filter) {
    return filter(item, query.value);
  }
  return item.label.toLowerCase().includes(query.value)
    || (item.subtitle ?? "").toLowerCase().includes(query.value);
}

const shownGroups = computed(() => allGroups.value
  .map(group => ({ group, items: group.items.filter(matches) }))
  .filter(entry => entry.items.length > 0));

function isItemDisabled(item: Item): boolean {
  return disabled || Boolean(item.disabled) || Boolean(item.disabledReason);
}

/** The rows "select all" acts on: the ones the search shows that the user may change. */
const toggleableValues = computed(() => shownGroups.value.flatMap(entry =>
  entry.items.filter(item => !isItemDisabled(item)).map(item => item.value)));
const selectedCount = computed(() => allValues.value.filter(value => selectedSet.value.has(value)).length);
const allToggleableSelected = computed(() =>
  toggleableValues.value.length > 0 && toggleableValues.value.every(value => selectedSet.value.has(value)));
const someToggleableSelected = computed(() => toggleableValues.value.some(value => selectedSet.value.has(value)));

const isEmpty = computed(() => allValues.value.length === 0);
const isSearchEmpty = computed(() => !isEmpty.value && shownGroups.value.length === 0);
const countText = computed(() => {
  if (selectedLabel !== undefined) {
    return selectedLabel;
  }
  return selectedCount.value > 0 ? t("base.selected_count", { count: selectedCount.value }) : null;
});
const showSelectAll = computed(() => selectAll && !isEmpty.value);

/**
 * Keeps the selection in list order, so for example exported columns follow the order on screen. Values that are
 * not in the list (rows of another page, or removed since) stay selected after the listed ones.
 */
function setSelection(values: Set<Item["value"]>) {
  const listed = new Set(allValues.value);
  selected.value = [
    ...allValues.value.filter(value => values.has(value)),
    ...selected.value.filter(value => !listed.has(value) && values.has(value)),
  ];
}

function toggleSelectAll() {
  const next = new Set(selectedSet.value);
  const select = !allToggleableSelected.value;
  for (const value of toggleableValues.value) {
    if (select) {
      next.add(value);
    } else {
      next.delete(value);
    }
  }
  setSelection(next);
}

function setItem(value: Item["value"], checked: boolean) {
  const next = new Set(selectedSet.value);
  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }
  setSelection(next);
}

/**
 * Two rows per line still get the list's divider above each line: every row after the first one on small screens,
 * every row after the first two from `md`.
 */
const twoColumnClass = "grid grid-cols-1 *:border-gray-200 md:grid-cols-2 dark:*:border-dark-600 [&>*+*]:border-t md:[&>:nth-child(2)]:border-t-0";

function checkboxId(value: Item["value"]): string {
  return `${a11yId}-item-${String(value)}`;
}

function groupLabelId(group: VCheckboxListGroup<Item>): string {
  return `${a11yId}-group-${group.key}`;
}
</script>

<template>
  <VListCard
    v-model:search="search"
    :searchable="searchable"
    :search-placeholder="searchPlaceholder"
    :title="selectAll ? undefined : title"
    :count="countText"
    :loading="loading"
    :empty="isEmpty"
    :empty-text="emptyText"
    :search-empty="isSearchEmpty"
    :search-empty-text="searchEmptyText"
    :max-height="maxHeight"
    :aria-label="selectAll ? title ?? ariaLabel : ariaLabel"
    :aria-describedby="ariaDescribedby"
    :invalid="invalid"
    :test-id="testId"
  >
    <template v-if="showSelectAll" #title>
      <VCheckbox
        :name="`${name}-select-all`"
        :model-value="allToggleableSelected"
        :indeterminate="someToggleableSelected && !allToggleableSelected"
        :disabled="toggleableValues.length === 0"
        :label-right="allToggleableSelected ? t('base.select_none') : t('base.select_all')"
        data-testid="checkbox-list-select-all"
        @update:model-value="toggleSelectAll"
      />
    </template>
    <template v-if="$slots['header-actions']" #header-actions>
      <slot name="header-actions" />
    </template>
    <template v-if="$slots.empty" #empty>
      <slot name="empty" />
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>

    <div
      v-for="entry in shownGroups"
      :key="entry.group.key"
      :role="entry.group.label ? 'group' : undefined"
      :aria-labelledby="entry.group.label ? groupLabelId(entry.group) : undefined"
      class="flex flex-col divide-y divide-gray-200 dark:divide-dark-600"
      data-testid="checkbox-list-group"
    >
      <VListCardRow v-if="entry.group.label" subheader data-testid="checkbox-list-group-header">
        <span class="flex min-w-0 items-center justify-between gap-3">
          <span :id="groupLabelId(entry.group)" class="min-w-0 truncate" :title="entry.group.label">{{ entry.group.label }}</span>
          <span class="shrink-0 tabular-nums">{{ entry.items.length }}</span>
        </span>
      </VListCardRow>

      <div :class="columns === 2 ? twoColumnClass : 'flex flex-col divide-y divide-gray-200 dark:divide-dark-600'">
        <VListCardRow
          v-for="item in entry.items"
          :key="String(item.value)"
          :title="item.label"
          :subtitle="item.subtitle ?? undefined"
          :control-id="checkboxId(item.value)"
          :disabled="isItemDisabled(item)"
          data-testid="checkbox-list-item"
        >
          <template #leading="{ titleId, subtitleId }">
            <VCheckbox
              :id="checkboxId(item.value)"
              :name="`${name}-${item.value}`"
              :model-value="selectedSet.has(item.value)"
              :disabled="disabled || item.disabled"
              :disabled-reason="item.disabledReason"
              :aria-labelledby="titleId"
              :aria-describedby="subtitleId"
              :aria-invalid="invalid || undefined"
              @update:model-value="checked => setItem(item.value, Boolean(checked))"
            />
            <slot name="item-prefix" :item="item" :group="entry.group" />
          </template>
          <template v-if="$slots['item-trailing']" #trailing>
            <slot name="item-trailing" :item="item" :group="entry.group" />
          </template>
        </VListCardRow>
      </div>
    </div>
  </VListCard>
</template>
